import { Stack, Select, Group, Button, ActionIcon, Text, TextInput, Divider } from '@mantine/core';
import { useController, useFieldArray, type Control } from 'react-hook-form';
import { IconPlus, IconX } from '@tabler/icons-react';
import { TagInput } from '@/shared/components/TagInput';
import { useTags } from '@/features/tag/useTags';
import {
  TAG_LOGICAL_OPERATORS,
  TAG_LOP_LABELS,
  LIST_SORT_UI_OPTIONS,
  PLAYLIST_CONSTRAINTS,
} from '@/lib/constants/playlist';
import type { SmartPlaylistFormValues } from '../schemas/PlaylistForm.schema';
import type { TagLogicalOperator } from '@/lib/constants/playlist';

interface TagRulesBuilderProps {
  control: Control<SmartPlaylistFormValues>;
  /** Live "Currently matches ~N ..." summary shown next to the heading (null hides it). */
  matchSummary?: string | null;
}

const LOP_ORDER: TagLogicalOperator[] = [...TAG_LOGICAL_OPERATORS];

const SORT_OPTIONS = LIST_SORT_UI_OPTIONS.map(o => ({
  value: o.value,
  label: o.label,
}));

export function TagRulesBuilder({ control, matchSummary }: TagRulesBuilderProps) {
  const { tags } = useTags();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tagRules',
  });

  const sortField = useController({ name: 'sortUIType', control });
  const limitField = useController({ name: 'limit', control });

  // Track used LOPs so each type can only appear once
  const usedLops = new Set(fields.map(f => f.lop));

  function handleAddRule() {
    const firstUnused = LOP_ORDER.find(lop => !usedLops.has(lop));
    if (firstUnused) {
      append({ lop: firstUnused, values: [] });
    }
  }

  // Hide "Add Rule" when all 3 LOPs are used OR max rules reached
  const canAddRule = fields.length < PLAYLIST_CONSTRAINTS.MAX_TAG_RULES
    && usedLops.size < TAG_LOGICAL_OPERATORS.length;

  return (
    <Stack gap="md">
      <Group gap="sm" align="baseline">
        <Text fw={600} size="md">Tag Rules</Text>
        {matchSummary && (
          <Text size="sm" c="dimmed">{matchSummary}</Text>
        )}
      </Group>

      {fields.map((field, index) => (
        <TagRuleRow
          key={field.id}
          control={control}
          index={index}
          currentLop={field.lop}
          usedLops={usedLops}
          allTags={tags}
          canRemove={fields.length > 1}
          onRemove={() => remove(index)}
        />
      ))}

      {canAddRule && (
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={handleAddRule}
          w="fit-content"
        >
          Add Rule
        </Button>
      )}

      <Divider />

      <Group gap="md" align="flex-start">
        <Select
          label="Sort Order"
          data={SORT_OPTIONS}
          value={sortField.field.value}
          onChange={(val) => val && sortField.field.onChange(val)}
          error={sortField.fieldState.error?.message}
          allowDeselect={false}
          w={280}
        />
        <TextInput
          label="Limit"
          placeholder="No limit"
          value={limitField.field.value}
          onChange={limitField.field.onChange}
          onBlur={limitField.field.onBlur}
          error={limitField.fieldState.error?.message}
          description={`Max ${PLAYLIST_CONSTRAINTS.MAX_LIMIT}`}
          w={120}
        />
      </Group>
    </Stack>
  );
}

/**
 * Single tag rule row — extracted to use hooks properly (no hooks in loops).
 * Each row has its own useController calls for lop and values fields.
 */
function TagRuleRow({
  control,
  index,
  currentLop,
  usedLops,
  allTags,
  canRemove,
  onRemove,
}: {
  control: Control<SmartPlaylistFormValues>;
  index: number;
  currentLop: TagLogicalOperator;
  usedLops: Set<TagLogicalOperator>;
  allTags: string[];
  canRemove: boolean;
  onRemove: () => void;
}) {
  const lopField = useController({
    name: `tagRules.${index}.lop` as const,
    control,
  });
  const valuesField = useController({
    name: `tagRules.${index}.values` as const,
    control,
  });

  // Show this row's current LOP + any unused LOPs
  const availableLops = LOP_ORDER
    .filter(lop => lop === currentLop || !usedLops.has(lop))
    .map(lop => ({ value: lop, label: TAG_LOP_LABELS[lop] }));

  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <Select
        w={160}
        data={availableLops}
        value={lopField.field.value}
        onChange={(val) => val && lopField.field.onChange(val)}
        allowDeselect={false}
      />
      <div style={{ flex: 1 }}>
        <TagInput
          value={valuesField.field.value}
          onChange={valuesField.field.onChange}
          onBlur={valuesField.field.onBlur}
          allTags={allTags}
          allowNewTags={false}
          maxTags={50}
          label=""
          description=""
          placeholder="Select tags..."
          error={valuesField.fieldState.error?.message}
        />
      </div>
      {canRemove && (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onRemove}
          aria-label="Remove rule"
          mt={4}
        >
          <IconX size={16} />
        </ActionIcon>
      )}
    </Group>
  );
}
